/**
 * ADDITIVE (T.Z171, owner 22.09) — the folded status explainer at the top of the
 * dashboard: «Как формируются статусы». A native `<details>` (the repo's own
 * disclosure pattern — `prefill-import__section`), closed by default, so a manager
 * sees one summary line until they ask for the rules.
 *
 * The text is a STATIC, co-located constant (`STATUS_EXPLAINER_SECTIONS`): no api
 * fetch, no props, no contract surface. EVERY sentence is derived from the rules as
 * they stand on 2026-09-30 (T.Z222 — the block was written 22.09 with T.Z171 and had
 * fallen behind: the mirror-as-primary pass, the virtual «Не прошёл ЕВИ» (T.Z204), the
 * inclusive-day decline deferral (T.Z194), the pgu-keyed decline arm (T.Z202), the twin
 * re-home (T.Z190/T.Z196 + the 30.09 owner directive) and the mirror's own «Зачислен»
 * (T.Z176) were all after it) — `docs/05-dev-spec.md` §4.1 (SR-6…SR-5 + the
 * rolling-queue guard), `packages/shared/src/status/` (`rules.ts`, `evi-failed.ts`,
 * `evi-not-passed.ts`), the worker's engines (`registry-status-engine.ts`,
 * `unti2035-sync.service.ts` — the pass, BOTH decline arms and the twin re-home), the
 * api's read folds (`displayStatusOf`, `contractDeadlineOf`, `eviNotPassedWhere`), the
 * LLM_CONTEXT 02/glossary facts («Дедлайн договора», «Договор подписан», «Согласие
 * родителей», «Провалил ЕВИ», «Не прошёл ЕВИ», the twin rule, «rolling queue») and
 * gotchas (bt)/(cd) for the two absence rules. When a status rule moves, THIS text has
 * to move with it — the file head says where each claim comes from, so the next edit
 * starts from the source, not from the prose.
 *
 * The module imports no stylesheet on purpose (the same reason `prefill-import-table`
 * was extracted out of its modal): the `unit` Playwright project renders it in plain
 * Node through `react-dom/server`, and a CSS import there has no loader. The styles
 * live in `dashboard.css`, which `DashboardPage` already pulls in.
 */

/** One unfolded block: a heading and the bullets under it. */
export interface StatusExplainerSection {
  /** Stable id — the section's `data-testid` suffix and React key. */
  id: string;
  heading: string;
  bullets: readonly string[];
}

/** The one line the closed block shows, next to the title. */
export const STATUS_EXPLAINER_SUMMARY =
  'От заявления на ЕПГУ до договора: этапы воронки, дедлайны, «Провалил ЕВИ», «Не прошёл ЕВИ», связь с УНТИ и вторые оси.';

/**
 * The seven sections of the explainer, in reading order. Kept as data (not JSX) so a
 * unit spec can pin the wording the same way `cards.ts` pins the card captions.
 */
export const STATUS_EXPLAINER_SECTIONS: readonly StatusExplainerSection[] = [
  {
    id: 'funnel',
    heading: 'Воронка: как заявление идёт по статусам',
    bullets: [
      '«Ждёт ссылку» — заявление есть на ЕПГУ («принято к рассмотрению» или «промежуточные результаты», коды 6 и 7), ссылка на ЕВИ ещё не выдана.',
      '«Получил ссылку» — ребёнок появился в свежей выгрузке ЕВИ. Лист ЕВИ — катящаяся очередь, а не журнал: строка уходит из него, когда ребёнок проходит конкурс, поэтому само по себе отсутствие в файле ни о чём не говорит.',
      '«Прошёл ЕВИ» — прохождение подтверждено источником (см. следующий раздел). С этого момента строка попадает в очередь на подписание договора — срок ищите в «Дедлайн договора» ниже.',
      'Боковые выходы. «Отказ» — терминальный вердикт, и приходит он из четырёх мест: код «4» на ЕПГУ; отказ самой УНТИ по заявлению ребёнка (их «declined») — мы применяем его только после конца дня «Автооткл.», последний день целиком остаётся ребёнку; дубль заявления ЕПГУ — когда ЕВИ прошёл ровно один из группы дублей, остальные уходят в «Отказ» и возвращаются, если пройдёт ещё один; подача, с которой лист УНТИ съехал на другое заявление (см. «Один лист УНТИ» ниже).',
      '«Ждёт доп. данных» — ЕПГУ ожидает дополнительную информацию по заявлению (код «14»). «Нет данных» — положительного сигнала нет нигде: в выгрузке ЕВИ ребёнка нет, в системе УНТИ нет, а код ЕПГУ движения не показывает.',
    ],
  },
  {
    id: 'passed',
    heading: '«Прошёл ЕВИ» и два дедлайна',
    bullets: [
      'Первичный источник — зеркало API УНТИ (cat.2035): заявление ребёнка появилось в их системе = прошёл (их список начинается со статуса «approved» — непрошедших в API не появляется). Резервный источник — FTP-выгрузка ЕВИ (unti /v1). Момент прохождения записывается один раз: кто первый проставил, тот и выиграл, второй источник его не переписывает.',
      '«Дедлайн ЕВИ» = дата ссылки + 10 дней — наш внутренний регламент на прохождение конкурса; показывается тем, кто ссылку получил, но ещё не прошёл.',
      // ADDITIVE (v1.9.66, T.Z204) — the deadline status the owner asked for.
      '«Не прошёл ЕВИ» — ссылка получена, но 10 дней истекли, а прохождения так и нет: строка краснеет в реестре и собирается фильтром статуса «Не прошёл ЕВИ». Это виртуальный статус, как и «Провалил ЕВИ»: хранимый статус остаётся «Получил ссылку», ничего не записывается, а подтверждённое прохождение возвращает строке её обычный вид. Если ребёнок «Провалил ЕВИ», показывается именно эта, более сильная пометка.',
      // ADDITIVE (v1.9.95, T.Z248) — the dashboard cards follow the same fold.
      'Карточки на дашборде считают по тем же правилам, что и реестр: «Получил ссылку» показывает тех, кто ещё в 10-дневном окне, просрочившие собраны в соседней карточке «Не прошёл ЕВИ», а клик по любой карточке открывает ровно те заявления, сколько она показывает.',
      '«Дедлайн договора» = дата автоотклонения заявления в УНТИ («Данные УНТИ» → «Автооткл.»), прочитанная у той подачи, на которую лист УНТИ указывает сейчас (см. «Один лист УНТИ» ниже): УНТИ само снимет заявление в этот момент, поэтому позже подписывать договор некогда. Часы идут только у прошедшего без подписанного договора: подписавшему показывается дата подписания, непрошедшему — «—». Если УНТИ о ребёнке молчит (нет зеркальной строки или даты), срок считается по-старому — 21 календарный день (те же 15 рабочих) с момента прохождения ЕВИ. Остаток дней считается по календарным датам: день дедлайна входит целиком — 25.09 при «сегодня 24.09» показывает «через 1 дн.», а «сегодня» и красный цвет — только в сам день дедлайна.',
    ],
  },
  {
    id: 'evi-failed',
    heading: '«Провалил ЕВИ» — пометка поверх статуса, а не этап воронки',
    bullets: [
      'Виртуальный статус: хранимый статус заявления не меняется, но в реестре, в фильтре и в карточке «Провалил ЕВИ» показывается как статус.',
      'Авто-пометка — тройное условие: ребёнок был в предыдущем файле ЕВИ → исчез из свежего → его нет в зеркале УНТИ (API). Нет unti_id — не помечаем никогда.',
      'Ручная пометка — администратор или менеджер ставит сам; машинные правила ручную отметку не трогают и не снимают.',
      'Авто-пометка снимается автоматически, когда прохождение подтверждается (API или FTP). Ручная остаётся за человеком.',
    ],
  },
  {
    id: 'link',
    heading: 'Один лист УНТИ — одно действующее заявление',
    bullets: [
      'У ребёнка может быть несколько заявлений на ЕПГУ, но лист в системе УНТИ один — и он указывает на ту подачу, которая сейчас действует. «Прошёл ЕВИ», «Автооткл.», «Зачислен» и оценки читаются у этой подачи, а не у той, где номер листа лежал раньше.',
      'Если лист переехал на другое заявление (например, родитель подал заново), прежняя подача получает «Отказ», а её производные факты — «Прошёл ЕВИ», «Зачислен» — с неё снимаются. Это слово самого УНТИ, система только переносит; правило работает и когда новая подача ещё не видна в выгрузках ЕПГУ и ЕВИ.',
      'Найти такие пары можно фильтром «УНТИ API» в реестре: «Найден» — лист и заявление совпадают; «Не найден по epgu_id + unti_id» — лист УНТИ смотрит на другое заявление.',
    ],
  },
  {
    id: 'guards',
    heading: 'Guard’ы: почему статус не откатывается вниз',
    bullets: [
      'Статус не откатывается вниз по лестнице при потере сигнала: исчезновение из выгрузки ЕВИ или из ЕПГУ не понижает статус — остаётся прежний (guard «катящейся очереди»: отсутствие — не доказательство).',
      'Терминальный вердикт ЕПГУ при этом не маскируется: «Отказ» и «Ждёт доп. данных» всегда доходят до строки, даже если ребёнок уже успел получить ссылку.',
      '«Отказ» держится записанным фактом, а не меткой: машинный отказ УНТИ пишет факт и статус вместе, поэтому повторные проходы выгрузки его не переигрывают; вердикт без факта за ним способен вернуться в «Прошёл ЕВИ».',
    ],
  },
  {
    id: 'axes',
    heading: 'Вторые оси: договор, согласие и зачисление (не этапы воронки)',
    bullets: [
      'Договор — «пришёл» / «подписан (дата)» / «не пришёл», приходит из платформы iSmart (CAT); синхронизация каждые 5 минут.',
      '«Договор подписан» = ребёнок нажал кнопку „Подписать“ в личном кабинете CAT. Документа-договора в системе не существует — платформа подтверждает шаг, а не подписанный документ.',
      '«Не пришёл» — прошёл ЕВИ, но на платформе его нет: это список на обзвон.',
      '«Получено согласие родителей» — отметка из той же платформы: «Отмечено» / «Не отмечено» / «—» (нет данных = платформа о ребёнке молчит; это не «не отмечено»). Согласие не зависит от договора.',
      '«Данные УНТИ» показывают их «Дату автоотклонения» («Автооткл.») — с решения владельца 24.09 это и есть наш «Дедлайн договора»: УНТИ снимает заявление в этот момент, значит это последний срок на подписание (как читаем — в разделе про дедлайны выше).',
      '«Зачислен» — слово самой УНТИ из того же зеркала: их статус «accepted» = «Да», любое другое слово = «Нет», зеркальной строки нет = «—». Факт живой: УНТИ передумает — колонка переиграет сама. Не путать с «Зачислены» в блоке «Договоры» ниже: тот счёт читает платформу iSmart, не УНТИ.',
      // T.Z249 (v1.10.0) — one sentence for the platform's OWN distribution fact (the
      // owner's rule: «Распределён» is the CLASS fact; their advisory materials flag
      // is stored but never gates it).
      '«Распределён» — факт класса: есть живой класс на платформе iSmart (или наша группа); их служебный флаг «материалы открыты» мы храним, но распределением не считаем, а «—» = сигнала нет ни с одной стороны.',
    ],
  },
  {
    id: 'where',
    heading: 'Где всё это смотреть',
    bullets: [
      'Реестр «Заявления»: колонки «Статус», «Дедлайн ЕВИ», «Договор», «Дедлайн договора» и оценки УНТИ; фильтр «Статус» знает и два виртуальных значения — «Провалил ЕВИ» и «Не прошёл ЕВИ»; рядом отдельные фильтры — «Провалил ЕВИ» (только авто-пометка), договор, согласие, зачисление, возраст и «УНТИ API»; кнопкой «Колонки» лишние колонки прячутся; в карточке заявления — журнал изменений.',
      '«Данные УНТИ»: их статус, «Подано», «Автооткл.» и наш статус рядом.',
      'Этот дашборд: карточки воронки считают хранимый статус (виртуальные статусы числа не двигают), карточка «Провалил ЕВИ» — только авто-пометку, «Зачислено» — слово УНТИ, блок «Договоры» — факты платформы iSmart.',
    ],
  },
];

/**
 * The block itself. Closed by default (no `open` attribute — the browser hides the
 * body, and the summary is the one line a manager reads on the way to the cards).
 */
export function StatusExplainer() {
  return (
    <details className="status-explainer" data-testid="status-explainer">
      <summary className="status-explainer__summary" data-testid="status-explainer-summary">
        <span className="status-explainer__chevron" aria-hidden="true">
          ›
        </span>
        <span className="status-explainer__title">Как формируются статусы</span>
        <span className="status-explainer__hint">{STATUS_EXPLAINER_SUMMARY}</span>
      </summary>
      <div className="status-explainer__body" data-testid="status-explainer-body">
        {STATUS_EXPLAINER_SECTIONS.map((section) => (
          <section
            key={section.id}
            className="status-explainer__section"
            data-testid={`status-explainer-section-${section.id}`}
          >
            <h3 className="status-explainer__heading">{section.heading}</h3>
            <ul className="status-explainer__list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </details>
  );
}
